import { useMemo } from 'react';

import {
  ChakraBox,
  ChakraText,
  DatePickerInput,
  FAIconName,
  FireflyButton,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { NotesSelect } from '~/components/maud/patients/note/NotesSelect';
import { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import {
  NO_NOTE_TEMPLATE_OPTION,
  NOTE_ATTACHMENT_SOURCES,
} from '~/lib/constants/notes';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { toMultiSelectOption } from '~/lib/util/menu';
import {
  useGetAdminNoteActivityWithNamesEnumQuery,
  useGetAdminNoteTypesEnumQuery,
  useGetNoteCommunicationMethodsEnumQuery,
} from '~/store/services/enumerations';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useFormikContext } from 'formik';
import { FieldErrorState } from '~/components/maud/patients/note/EditNoteForm';
import NoteAttachmentMenu from './NoteAttachmentMenu';

type Props = {
  id: string;
  activityWithOptions: NoteMultiSelectOption[];
  isNoteAuthor: boolean;
  onFormChange: () => void;
  patientId?: string;
  onUploadSuccess?: (docs: AttachmentType[]) => void;
  attachedDocuments?: AttachmentType[];
};
export const PatientEditNoteFormAttributesRow = ({
  id,
  activityWithOptions,
  isNoteAuthor,
  onFormChange,
  patientId,
  onUploadSuccess,
  attachedDocuments,
}: Props) => {
  const {
    refreshNoteTemplateRevisionData,
    isRefreshNoteTemplateRevisionDataFetching,
  } = useEditPatientNoteFormContext();

  const { values: formValues } = useFormikContext<EditNoteFormData>();

  const { fieldErrors, setFieldErrors, setFormField } =
    useEditPatientNoteFormContext();

  const { activityDate, noteTemplateRevision } = formValues;

  const { data: adminNoteTypes, isLoading: isGetAdminNoteTypesLoading } =
    useGetAdminNoteTypesEnumQuery();
  const {
    data: noteCommunicationMethods,
    isLoading: isGetNoteCommunicationMethodsLoading,
  } = useGetNoteCommunicationMethodsEnumQuery();

  const { isLoading: isGetAdminNoteActivityWithNamesLoading } =
    useGetAdminNoteActivityWithNamesEnumQuery();

  const activityMediumReasons = useMemo(
    () => (noteCommunicationMethods ?? []).map(toMultiSelectOption),
    [noteCommunicationMethods],
  );

  const adminNoteTypeOptions = useMemo(
    () => (adminNoteTypes || []).map(toMultiSelectOption),
    [adminNoteTypes],
  );

  // the appointment date can be in future, so take the max date based on the activityDate
  const maxDate = useMemo(
    () =>
      formValues.activityDate > DateTime.now().toJSDate()
        ? formValues.activityDate
        : DateTime.now().toJSDate(),
    [formValues?.activityDate],
  );

  return (
    <ChakraBox
      alignItems="center"
      display="flex"
      gap="10px"
      marginBottom="12px"
    >
      <NotesSelect
        ariaLabel="Note type"
        error={fieldErrors.typeError}
        fieldName="type"
        isDisabled={isGetAdminNoteTypesLoading}
        isLoading={isGetAdminNoteTypesLoading}
        isRequired
        onFormChange={onFormChange}
        options={adminNoteTypeOptions ?? []}
        placeholder="Note type (required)"
        setFieldValue={setFormField}
        updateFieldErrorReducer={(newError: Partial<FieldErrorState>) =>
          setFieldErrors((prev) => ({ ...prev, newError }))
        }
        values={formValues}
      />
      <ChakraText fontSize="sm">via</ChakraText>
      <NotesSelect
        ariaLabel="Activity medium"
        fieldName="medium"
        isDisabled={isGetNoteCommunicationMethodsLoading}
        isLoading={isGetNoteCommunicationMethodsLoading}
        minWidth="150px"
        onFormChange={onFormChange}
        options={activityMediumReasons ?? []}
        placeholder="medium"
        setFieldValue={setFormField}
        values={formValues}
      />
      <ChakraText fontSize="sm">on</ChakraText>
      <DatePickerInput
        defaultDate={activityDate ? activityDate : undefined}
        id={`${id}__activity-date`}
        inputVariant="slim"
        isInvalid={Boolean(fieldErrors.dateError)}
        maxDate={maxDate}
        onSubmit={(date: Date) => {
          setFormField('activityDate', date);
          setFieldErrors((prev) => ({ ...prev, dateError: null }));
        }}
        placeholder="date (required)"
        rightIcon={FAIconName.chevronDownRegular}
        showTimeInput
      />
      <ChakraText fontSize="sm">with</ChakraText>
      <NotesSelect
        ariaLabel="Activity with"
        fieldName="activity"
        isDisabled={isGetAdminNoteActivityWithNamesLoading}
        isLoading={isGetAdminNoteActivityWithNamesLoading}
        minWidth="150px"
        multiple
        onFormChange={onFormChange}
        options={activityWithOptions ?? []}
        placeholder="participants"
        setFieldValue={setFormField}
        values={formValues}
      />
      {isNoteAuthor && (
        <NoteAttachmentMenu
          attachedDocuments={attachedDocuments}
          id={id}
          source={NOTE_ATTACHMENT_SOURCES.NOTE_TAB}
          onUploadSuccess={onUploadSuccess}
          patientId={patientId}
        />
      )}

      {noteTemplateRevision?.node &&
        noteTemplateRevision?.node !== NO_NOTE_TEMPLATE_OPTION.node && (
          <FireflyButton
            id={`${id}-refresh-note-template-data`}
            isLoading={isRefreshNoteTemplateRevisionDataFetching}
            leftIcon={FAIconName.refreshRegular}
            onClick={refreshNoteTemplateRevisionData}
            size="small"
            variant="tertiary"
          >
            Refresh data
          </FireflyButton>
        )}
    </ChakraBox>
  );
};
